import {
  CreateInsurancePolicyParticipantRequest,
  CreateInsurancePolicyRequest,
} from 'shared/api/insurance'
import { preparePhoneNumberForRequest } from 'shared/lib/prepare-phone-number'

import {
  InsuranceDraft,
  InsuranceParticipantData,
  InsurancePolicyholderData,
} from './types'

const PASSPORT_PATTERN = /^(\p{L}*)(\d+)$/u

interface InsurancePassport {
  series: string
  number: string
}

export const parseInsurancePassport = (
  value: string,
): InsurancePassport | null => {
  const match = value.replace(/[\s-]/g, '').match(PASSPORT_PATTERN)

  if (!match) return null

  return {
    series: match[1].toUpperCase(),
    number: match[2],
  }
}

const mapParticipant = (
  participant: InsuranceParticipantData,
  phone: string | null = null,
): CreateInsurancePolicyParticipantRequest | null => {
  const passport = parseInsurancePassport(participant.passportNumber)

  if (
    !passport ||
    !participant.inn.trim() ||
    !participant.lastName.trim() ||
    !participant.firstName.trim() ||
    !participant.birthDate
  ) {
    return null
  }

  return {
    inn: participant.inn.trim(),
    first_name: participant.firstName.trim(),
    last_name: participant.lastName.trim(),
    date_birth: participant.birthDate,
    passport_series: passport.series,
    passport_number: passport.number,
    phone,
    citizenship_id: participant.citizenship?.id || null,
  }
}

const mapParticipants = (
  policyholder: InsurancePolicyholderData,
  policyholderPhone: string,
  participants: InsuranceParticipantData[],
): CreateInsurancePolicyParticipantRequest[] | null => {
  const mappedParticipants = [
    mapParticipant(policyholder, policyholderPhone),
    ...participants.map((participant) => mapParticipant(participant)),
  ]

  return mappedParticipants.every(
    (participant): participant is CreateInsurancePolicyParticipantRequest =>
      participant !== null,
  )
    ? mappedParticipants
    : null
}

const hasPolicyholderData = (
  policyholder: InsurancePolicyholderData,
): boolean =>
  Boolean(
    policyholder.inn.trim() &&
    policyholder.lastName.trim() &&
    policyholder.firstName.trim() &&
    policyholder.birthDate &&
    policyholder.citizenship?.id,
  )

export const mapInsuranceDraftToPolicyRequest = (
  draft: InsuranceDraft,
): CreateInsurancePolicyRequest | null => {
  const { country, program, purpose, policyholder, startDate, endDate } = draft

  if (
    !country ||
    !program ||
    !purpose ||
    !policyholder ||
    !startDate ||
    !endDate ||
    !hasPolicyholderData(policyholder)
  ) {
    return null
  }

  const passport = parseInsurancePassport(policyholder.passportNumber)
  const phone = preparePhoneNumberForRequest(policyholder.phone)
  const citizenshipId = policyholder.citizenship?.id

  if (!passport || !phone || !citizenshipId) return null

  const participants = mapParticipants(policyholder, phone, draft.participants)

  if (!participants) return null

  return {
    client_type: 1,
    country_ids: [country.id],
    program_id: program.id,
    purpose_id: purpose.id,
    citizenship_id: citizenshipId,
    inn: policyholder.inn.trim(),
    first_name: policyholder.firstName.trim(),
    last_name: policyholder.lastName.trim(),
    date_birth: policyholder.birthDate,
    passport_series: passport.series,
    passport_number: passport.number,
    address: policyholder.address?.trim() || '',
    phone,
    start_date: startDate,
    end_date: endDate,
    participants,
  }
}
